import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnChanges,
	SimpleChanges,
	Inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BadgeModule } from '@go-modules/components/badge/badge.module';
import { AiMarkerSetPanelComponent } from '@go-modules/components/ai-marker-set-panel/ai-marker-set-panel.component';
import { BadgeColors } from '@go-modules/components/badge/badge.constants';
import type { LlmDefinition } from '@go-modules/interfaces/llm-definition';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { AIMarkersDialogComponent } from '@go-modules/components/dialogs/ai-markers-dialog/ai-markers-dialog.component';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { mediaPreviewToken } from 'go-modules/modals/media-preview/modal.factory';
import { ProductTier } from '@go-modules/interfaces/licenses/product-tier';

@Component({
	selector: 'activity-edit-ai-markers-section',
	standalone: true,
	imports: [
		CommonModule,
		BadgeModule,
		AiMarkerSetPanelComponent,
		TranslateModule
	],
	templateUrl: './activity-edit-ai-markers-section.component.html',
	styleUrls: ['./activity-edit-ai-markers-section.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityEditAiMarkersSectionComponent implements OnChanges {
	@Input() public activity: ActivityModel | null = null;

	public llmDefinitions: LlmDefinition[] = [];
	public aiTranscriptionLanguage: string | null = null;
	public readonly canPreviewAiPrompts: boolean = true;
	private initialLlmDefinitions: LlmDefinition[] = [];
	private initialAiTranscriptionLanguage: string | null = null;
	private initialAiPromptsEnabled: boolean = false;

	public readonly badgeColors = BadgeColors;

	constructor (
		private activityFormService: ActivityFormService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		private goModalService: GoModalService,
		private translate: TranslateService,
		@Inject(mediaToken) private MediaModel,
		@Inject(mediaPreviewToken) private mediaPreviewModal,
		private cdr: ChangeDetectorRef
	) {}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.activity?.currentValue) {
			this.initializeFromActivity();
		}
	}

	public get hasDefinitions (): boolean {
		return !!this.llmDefinitions?.length;
	}

	public shouldShowAiMarkerSection (): boolean {
		const template = this.activity?.template;
		if (!template || !template.isActive?.('ai_prompts_enabled')) {
			return false;
		}

		const license = this.selectedService.getLicense();
		const salesforceLicense = license?.salesforce_license;

		if (!license || salesforceLicense?.may_tease) {
			return true;
		}

		return !!salesforceLicense?.ai_prompts_enabled;
	}

	public shouldShowAdvancedPlanBadge (): boolean {
		const license = this.selectedService.getLicense();
		if (!license) {
			return true;
		}
		const salesforceLicense = license.salesforce_license;
		if (!salesforceLicense) {
			return true;
		}

		return salesforceLicense.tier_name === ProductTier.Standard ||
			salesforceLicense.tier_name === null ||
			salesforceLicense.is_free_trial;
	}

	public openAiMarkers (): void {
		if (!this.activity) {
			return;
		}

		this.goModalService.open(AIMarkersDialogComponent, true, {
			data: {
				activity: this.activity,
				llmDefinitions: structuredClone(this.llmDefinitions ?? []),
				aiTranscriptionLanguage: this.aiTranscriptionLanguage
			}
		}).afterClosed().subscribe((result) => {
			if (!result) {
				return;
			}
			this.llmDefinitions = structuredClone(result.llm_definitions ?? []);
			this.aiTranscriptionLanguage = result.ai_transcription_language ?? null;
			this.updateAiControls(true);
			this.cdr.markForCheck();
		});
	}

	public removeAiMarkerSet (): void {
		this.llmDefinitions = [];
		this.aiTranscriptionLanguage = null;
		this.updateAiControls(true);
		this.cdr.markForCheck();
	}

	public previewAiMarkers (): void {
		const media = this.MediaModel.model({
			media_status: this.MediaModel.READY,
			media_type: this.MediaModel.TYPE.DOCUMENT,
			filename: 'ai-preview.gif',
			media_url: 'https://staticassets.goreact.com/ai-preview.gif',
			title: this.translate.instant('activity-info_example-ai-markers')
		});

		this.mediaPreviewModal.open({
			modalData: {
				media,
				edit: false,
				allowDownload: false
			}
		});
	}

	public resetSelections (): void {
		this.llmDefinitions = structuredClone(this.initialLlmDefinitions ?? []);
		this.aiTranscriptionLanguage = this.initialAiTranscriptionLanguage ?? null;
		const resolvedState = this.updateAiControls(false, this.initialAiPromptsEnabled);
		this.initialLlmDefinitions = structuredClone(this.llmDefinitions ?? []);
		this.initialAiTranscriptionLanguage = this.aiTranscriptionLanguage;
		this.initialAiPromptsEnabled = resolvedState;
		this.cdr.markForCheck();
	}

	private initializeFromActivity (): void {
		if (!this.activity) {
			return;
		}

		this.llmDefinitions = structuredClone(this.activity.llm_definitions ?? []);
		this.aiTranscriptionLanguage = this.activity.ai_transcription_language ?? null;
		this.initialLlmDefinitions = structuredClone(this.llmDefinitions ?? []);
		this.initialAiTranscriptionLanguage = this.aiTranscriptionLanguage;
		this.initialAiPromptsEnabled = !!this.activity.ai_prompts_enabled || this.hasAiSelections();
		const resolvedState = this.updateAiControls(false, this.initialAiPromptsEnabled);
		this.initialAiPromptsEnabled = resolvedState;
		this.cdr.markForCheck();
	}

	private updateAiControls (markDirty: boolean, aiPromptsOverride?: boolean): boolean {
		const llmControl = this.activityFormService.getControl('llm_definitions');
		const languageControl = this.activityFormService.getControl('ai_transcription_language');
		const aiPromptsControl = this.activityFormService.getControl('ai_prompts_enabled');
		const llmDefinitionsClone = structuredClone(this.llmDefinitions ?? []);
		const shouldEnableAiPrompts = typeof aiPromptsOverride === 'boolean'
			? aiPromptsOverride
			: this.hasAiSelections();

		llmControl?.setValue(llmDefinitionsClone);
		languageControl?.setValue(this.aiTranscriptionLanguage);
		aiPromptsControl?.setValue(shouldEnableAiPrompts);

		if (markDirty) {
			llmControl?.markAsDirty();
			languageControl?.markAsDirty();
			aiPromptsControl?.markAsDirty();
			this.activityFormService.markFormAsDirty();
		} else {
			llmControl?.markAsPristine();
			languageControl?.markAsPristine();
			aiPromptsControl?.markAsPristine();
		}

		if (this.activity) {
			this.activity.llm_definitions = structuredClone(llmDefinitionsClone);
			this.activity.ai_transcription_language = this.aiTranscriptionLanguage;
			this.activity.ai_prompts_enabled = shouldEnableAiPrompts;
		}

		return shouldEnableAiPrompts;
	}

	private hasAiSelections (): boolean {
		return (this.llmDefinitions?.length ?? 0) > 0;
	}
}
